package ai.wagemix.web.screens.mixes

import kotlin.math.roundToInt
import ai.wagemix.web.screens.WMLockedState
import ai.wagemix.logic.mixes.WMSavedMixCard
import ai.wagemix.logic.mixes.WMSavedMixesFilter
import ai.wagemix.logic.mixes.WMSavedMixesPresenter
import ai.wagemix.logic.mixes.WMSavedMixesPresenter.Action
import ai.wagemix.logic.mixes.WMSavedMixesPresenter.Event
import ai.wagemix.logic.mixes.WMSavedMixesPresenter.Prompt
import ai.wagemix.web.components.WMButton
import ai.wagemix.web.components.WMButtonKind
import ai.wagemix.web.components.WMButtonSize
import ai.wagemix.web.components.WMDialog
import ai.wagemix.web.components.WMDialogButtonStyle
import ai.wagemix.web.components.WMEmptyState
import ai.wagemix.web.components.WMField
import ai.wagemix.web.components.WMMenuButton
import ai.wagemix.web.components.WMMenuItem
import ai.wagemix.web.components.WMIconGlyph
import ai.wagemix.web.components.WMMixCard
import ai.wagemix.web.components.WMMixCardOccupation
import ai.wagemix.web.components.WMMixCardPresentation
import ai.wagemix.web.components.WMSegmentedControl
import ai.wagemix.web.components.WMTextField
import ai.wagemix.web.components.of
import ai.wagemix.web.screens.WMWebStrings
import ai.wagemix.web.screens.formatWageShort
import ai.wagemix.web.screens.sparklineModel
import ai.wagemix.web.theme.WMBodySmallTextStyle
import ai.wagemix.web.theme.WMCardGridGapVar
import ai.wagemix.web.theme.WMMixCardWidthVar
import ai.wagemix.web.theme.WMAppContentGapVar
import androidx.compose.runtime.Composable
import org.jetbrains.compose.web.dom.Div
import com.varabyte.kobweb.compose.ui.toAttrs
import androidx.compose.runtime.State
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.fsryan.foundation.logic.FSPrompt
import com.fsryan.foundation.logic.prompting.FSPromptingPresenter
import com.fsryan.foundation.web.components.FSLoadingIndicator
import ai.wagemix.web.screens.WMPromptDialog
import com.fsryan.foundation.web.screens.FSWebScreen
import com.fsryan.foundation.web.theme.fs
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.gridTemplateColumns
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.fr
import org.jetbrains.compose.web.css.px
import kotlin.time.Clock
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime

val WMMixGridStyle = CssStyle.base {
    Modifier.fillMaxWidth().display(DisplayStyle.Grid)
        .gridTemplateColumns { repeat(autoFill) { minmax(WMMixCardWidthVar.value(), 1.fr) } }
        .gap(WMCardGridGapVar.value())
}

@Composable
fun <P: Prompt, E: Event<P>, A: Action, PR: WMSavedMixesPresenter<P, E, A>, VM: WMBaseSavedMixesWebViewModel<P, E, A, PR>> WMSavedMixesScreen(
    createViewModel: () -> VM,
    strings: WMWebStrings,
    modifier: Modifier = Modifier
) {
    FSWebScreen(createViewModel = createViewModel) { vm ->
        WMSavedMixesScreenLayout(
            strings = strings,
            loadingState = vm.loadingState,
            cardsState = vm.cardsState,
            hasMixesState = vm.hasMixesState,
            filterState = vm.filterState,
            queryState = vm.queryState,
            lapsedState = vm.lapsedState,
            modifier = modifier,
            onListParametersChange = vm::userChangedListParameters,
            onOpen = vm::userRequestedOpen,
            onShare = vm::userRequestedShare,
            onRename = vm::userRequestedRename,
            onDuplicate = vm::userRequestedDuplicate,
            onDelete = vm::userRequestedDelete
        )
        val prompt = vm.promptState.value
        WMSavedMixesPromptDialog(
            prompt = prompt,
            strings = strings,
            onResponse = { selected -> vm.userRespondedToPrompt(FSPrompt.Response(prompt, selected)) },
            onConfirmRename = vm::userConfirmedRename
        )
    }
}

@Composable
fun WMSavedMixesScreenLayout(
    strings: WMWebStrings,
    loadingState: State<Boolean>,
    cardsState: State<List<WMSavedMixCard>>,
    hasMixesState: State<Boolean>,
    filterState: State<WMSavedMixesFilter>,
    queryState: State<String>,
    lapsedState: State<Boolean>,
    modifier: Modifier = Modifier,
    onListParametersChange: (WMSavedMixesFilter, String) -> Unit,
    onOpen: (String) -> Unit,
    onShare: (String) -> Unit,
    onRename: (String) -> Unit,
    onDuplicate: (String) -> Unit,
    onDelete: (String) -> Unit
) {
    val s = strings.savedMixes
    val colors = ColorMode.current.toPalette().fs
    val filter = filterState.value
    val query = queryState.value
    if (lapsedState.value) {
        WMLockedState(strings.renewal.savedMixesTitle, strings.renewal.savedMixesMessage, strings, modifier)
        return
    }
    Column(modifier = Modifier.fillMaxWidth().gap(WMAppContentGapVar.value()).then(modifier)) {
        // The search on top, the All / From job descriptions filter under it.
        Column(modifier = Modifier.fillMaxWidth().gap(12.px)) {
            WMTextField(
                query,
                onTextChange = { onListParametersChange(filter, it) },
                placeholder = s.searchPlaceholder,
                leadingIcon = WMIconGlyph.Search,
                modifier = Modifier.maxWidth(320.px)
            )
            WMSegmentedControl(
                options = listOf(s.all, s.fromJobDescriptions),
                selectedIndex = filter.ordinal,
                onSelect = { onListParametersChange(WMSavedMixesFilter.entries[it], query) },
                accessibilityString = s.title
            )
        }
        if (loadingState.value) FSLoadingIndicator(strings.common.loading)
        val cards = cardsState.value
        when {
            !hasMixesState.value -> WMEmptyState(WMIconGlyph.Bookmark, s.emptyTitle, s.emptyMessage) {
                WMButton(strings.shell.newMix, leadingIcon = WMIconGlyph.Plus) { kotlinx.browser.window.location.href = ai.wagemix.logic.WMRoutes.MIX }
            }
            cards.isEmpty() -> SpanText(s.noMatches, WMBodySmallTextStyle.toModifier().color(colors.onSurfaceVariant))
            else -> Div(WMMixGridStyle.toModifier().toAttrs()) {
                val year = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).year
                cards.forEach { card ->
                    // An AI mix can't be deleted: it stands for one of the plan's job-description analyses.
                    val menuItems = listOfNotNull(
                        WMMenuItem(s.rename, WMIconGlyph.Pencil) { onRename(card.mixId) },
                        WMMenuItem(s.duplicate, WMIconGlyph.Copy) { onDuplicate(card.mixId) },
                        WMMenuItem(s.delete, WMIconGlyph.Trash2, destructive = true) { onDelete(card.mixId) }
                            .takeUnless { card.fromJobDescription },
                    )
                    WMMixCard(
                        mix = WMMixCardPresentation.of(
                            mixId = card.mixId,
                            title = card.title,
                            meta = "${card.occupationCount} ${s.occupations}",
                            fromJobDescription = card.fromJobDescription,
                            occupations = card.topOccupations.map {
                                WMMixCardOccupation.of(weightText = "${it.percentage.roundToInt()}%", name = it.name)
                            },
                            medianText = card.median?.let { formatWageShort(it, hourly = false) },
                            medianCaption = s.medianCaption,
                            sparkline = card.sparkline.takeIf { it.size >= 2 }?.let { sparklineModel(it, year - it.size + 1) }
                        ),
                        strings = s.card,
                        menu = { WMMenuButton(accessibilityString = s.more, items = menuItems) }
                    ) {
                        WMButton(s.share, kind = WMButtonKind.Secondary, size = WMButtonSize.Sm, leadingIcon = WMIconGlyph.Share2) { onShare(card.mixId) }
                        WMButton(s.open, size = WMButtonSize.Sm) { onOpen(card.mixId) }
                    }
                }
            }
        }
    }
}

@Composable
fun <P: Prompt> WMSavedMixesPromptDialog(
    prompt: P,
    strings: WMWebStrings,
    onResponse: (Int) -> Unit,
    onConfirmRename: (mixId: String, title: String) -> Unit
) {
    val s = strings.savedMixes
    WMPromptDialog(prompt = prompt, unwrap = { (it as? Prompt.ComposedSuperPrompt)?.wrapped }, strings = strings, onResponse = onResponse) { p ->
        when (p) {
            is Prompt.RenameMix -> {
                var title by remember(p) { mutableStateOf(p.currentTitle) }
                WMDialog(
                    title = s.renameTitle,
                    onDismiss = { onResponse(Prompt.RenameMix.Selection.Cancel.ordinal) },
                    forceInteraction = true,
                    actions = {
                        WMButton(strings.common.cancel, kind = WMButtonKind.Ghost, modifier = WMDialogButtonStyle.toModifier()) { onResponse(Prompt.RenameMix.Selection.Cancel.ordinal) }
                        WMButton(s.rename, modifier = WMDialogButtonStyle.toModifier(), enabled = title.isNotBlank()) { onConfirmRename(p.mixId, title) }
                    }
                ) {
                    WMField(strings.mixBuilder.mixTitle) { WMTextField(title, onTextChange = { title = it }) }
                }
            }
            is Prompt.DeleteMix -> WMDialog(
                title = s.deleteTitle,
                description = "${p.title}: ${s.deleteDescription}",
                onDismiss = { onResponse(Prompt.DeleteMix.Selection.Cancel.ordinal) },
                forceInteraction = true,
                actions = {
                    WMButton(strings.common.cancel, kind = WMButtonKind.Ghost, modifier = WMDialogButtonStyle.toModifier()) { onResponse(Prompt.DeleteMix.Selection.Cancel.ordinal) }
                    WMButton(s.delete, kind = WMButtonKind.Danger, modifier = WMDialogButtonStyle.toModifier()) { onResponse(Prompt.DeleteMix.Selection.Delete.ordinal) }
                }
            ) {}
            else -> Unit
        }
    }
}
